/**
 * Shell aplikasi: sidebar di desktop, bottom tab bar di mobile.
 *
 * Layout ini tidak merender header sendiri. Setiap halaman memakai
 * `PageHeader` karena isinya berbeda jauh antar layar: Beranda butuh sapaan +
 * avatar, Kamus butuh judul + pencarian, wizard Kontributor butuh indikator
 * langkah. Yang di sini hanya kerangka, navigasi global, dan tombol tema.
 *
 * Catatan: `NavBar.tsx` dan `Footer.tsx` versi lama sudah DIHAPUS — keduanya
 * hanya berisi navigasi atas yang tidak dipakai lagi. Jangan diimpor dari mana
 * pun; footer sekarang bagian dari Layout di bawah.
 */

import { useEffect, useState, useSyncExternalStore } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { session, rehydrate } from '../lib/session'
import { apiPost } from '../lib/api'
import { BottomTabBar, SideNav } from './Nav'
import AuroraBackdrop from './AuroraBackdrop'

// Atribusi lisensi wajib (lihat docs/API_CONTRACT.md / footer): konten kamus
// berasal dari id.wiktionary.org di bawah CC BY-SA 4.0.
const WIKTIONARY_LICENCE_URL = 'https://creativecommons.org/licenses/by-sa/4.0/'

type Theme = 'light' | 'dark'

/**
 * Baca tema awal dengan urutan: pilihan eksplisit di localStorage -> atribut
 * `data-theme` -> preferensi sistem.
 *
 * localStorage HARUS dibaca lebih dulu. `data-theme` hanya diisi efek useEffect
 * setelah mount, jadi kalau halaman dimuat ulang atributnya kosong; membaca
 * atribut lebih dulu membuat pilihan user tertimpa preferensi sistem.
 */
function readInitialTheme(): Theme {
  try {
    const saved = window.localStorage.getItem('kb-theme')
    if (saved === 'light' || saved === 'dark') return saved
  } catch {
    // localStorage bisa diblokir (mode privat) - lanjut ke atribut/sistem.
  }
  const attr = document.documentElement.dataset.theme
  if (attr === 'light' || attr === 'dark') return attr
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** Baca user yang sedang login dari store modul `lib/session.ts`. */
function useSessionUser() {
  return useSyncExternalStore(
    (onChange) => session.subscribe(onChange),
    () => session.getUser(),
    () => null,
  )
}

export default function Layout({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readInitialTheme)
  const user = useSessionUser()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)

  // Rehidrasi sesi saat halaman dimuat: cookie HttpOnly tidak bisa dibaca
  // JavaScript, jadi satu-satunya cara tahu status login adalah bertanya ke
  // /api/auth/me. Tanpa ini, pengunjung yang sebenarnya sudah login tetap
  // sebagai anonim sampai mereka me-refresh halaman login.
  //
  // Logikanya tinggal di `session.rehydrate()`. Dulu salinan logika ini juga
  // ada di AdminShell, dan saat panel dipindahkan keluar dari Layout, salinan
  // itu tidak ikut ter-update - sesi menggantung. Sekarang hanya ada satu.
  useEffect(() => {
    void rehydrate()
  }, [])

  async function handleLogout() {
    setBusy(true)
    try {
      await apiPost('/auth/logout')
    } catch {
      // Logout gagal di jaringan: tetap keluarkan user dari store supaya UI
      // tidak menampilkan menu yang tidak akan bekerja.
    } finally {
      session.setUser(null)
      setBusy(false)
      navigate('/')
    }
  }

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem('kb-theme', theme)
    } catch {
      // localStorage bisa diblokir (mode privat); tema tetap berlaku di sesi ini.
    }
  }, [theme])

  const sidebarFooter = (
    <div className="flex flex-col gap-3">
      {/* Nav menyesuaikan status sesi: jangan tawarkan "Masuk"/"Daftar" ke orang
          yang sudah login, dan jangan withhold "Keluar" dari orang yang belum
          punya akun. */}
      {user ? (
        <>
          <Link to="/profil" className="flex items-center gap-2 text-sm">
            <span
              aria-hidden="true"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--kb-brand-chip-bg)] text-sm font-semibold text-[var(--kb-brand-chip-ink)]"
            >
              {user.display_name.slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 truncate">{user.display_name}</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            disabled={busy}
            className="btn-secondary !px-3 !py-1.5"
          >
            {busy ? 'Keluar...' : 'Keluar'}
          </button>
        </>
      ) : (
        <>
          <Link to="/masuk" className="btn-secondary !py-1.5">
            Masuk
          </Link>
          <Link to="/daftar" className="btn-primary !py-1.5">
            Daftar
          </Link>
        </>
      )}

      <button
        type="button"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        aria-label={theme === 'dark' ? 'Ganti ke mode terang' : 'Ganti ke mode gelap'}
        className="rounded border border-[var(--kb-border)] px-2 py-1.5 text-sm hover:bg-[var(--kb-surface-2)]"
      >
        {theme === 'dark' ? 'Mode terang' : 'Mode gelap'}
      </button>
    </div>
  )

  return (
    <>
      {/*
        Wallpaper aurora. Tanpa `bg-[var(--kb-bg)]` pada pembungkus di bawah,
        kanvas memakai warna latarnya sendiri supaya transisi tema mulus; begitu
        ada latar padat, kanvas akan tertutup dan efeknya hilang.
      */}
      <AuroraBackdrop />

      <div className="app-shell flex min-h-screen bg-transparent text-[var(--kb-ink)]">
      <a
        href="#konten"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-[var(--kb-surface)] focus:px-3 focus:py-2"
      >
        Lewati ke konten
      </a>

      <SideNav footer={sidebarFooter} />

      {/* `pb-24` memberi ruang untuk tab bar yang sekarang MENGAMBANG. Tanpa
         ruang ekstra ini, kartu terakhir tertutup bar navigasi - dan karena bar
         tidak lagi menempel ke tepi bawah, celah yang diperlukan lebih besar
         daripada saat ia flush. */}
      <div className="flex min-w-0 flex-1 flex-col pb-24 md:pb-0">
        <main id="konten" className="min-w-0 flex-1 px-4 py-5 sm:px-6 md:py-8">
          {children}
        </main>

        {/*
          Footer memakai `backdrop-blur`, bukan latar solid: solid akan menutup
          aurora persis di bagian paling bawah halaman yang biasanya terlihat
          saat scroll. Kabut + warna transparan menjaga teks tetap kontras.
        */}
        <footer className="app-footer bg-[var(--kb-surface)]/80 backdrop-blur-md">
          <div className="mx-auto flex w-full max-w-4xl flex-col gap-1 px-4 py-4 text-sm text-[var(--kb-muted)] sm:flex-row sm:flex-wrap sm:gap-x-6">
            <span>Kamus Nusantara - kamus bahasa daerah Indonesia.</span>
            <Link to="/kamus" className="hover:underline">
              Jelajahi kamus
            </Link>
            <Link to="/tentang" className="hover:underline">
              Tentang
            </Link>
          </div>
          <div className="mx-auto w-full max-w-4xl border-t border-[var(--kb-border)] px-4 py-3 text-xs text-[var(--kb-muted)]">
            Konten kamus dari{' '}
            <a
              href="https://id.wiktionary.org"
              target="_blank"
              rel="noreferrer noopener"
              className="link"
            >
              id.wiktionary.org
            </a>
            , lisensi{' '}
            <a
              href={WIKTIONARY_LICENCE_URL}
              target="_blank"
              rel="license noopener noreferrer"
              className="link"
            >
              CC BY-SA 4.0
            </a>
            .
          </div>
        </footer>
      </div>

      <BottomTabBar />
      </div>
    </>
  )
}